import { useCallback, useMemo } from 'react'
import {
  IUseTreatmentWithConfigResult,
  useSplitClient,
  useTreatmentWithConfig,
} from '@splitsoftware/splitio-react'

import { useSeller } from '@/contexts/seller'
import { FeatureToggle } from '@/resources/enums/featureToggle'
import { SELLER_DATA } from '@/resources/utils'

type Props = {
  splitName: string
  attributes?: object
  includeLoadingState?: boolean
  includeConfig?: boolean
}

type StoredSellerData = {
  seller?: {
    id?: number | string
    address?: {
      provinceCode?: string
      city?: string
    }
  }
}

const EMPTY_SPLIT_ATTRIBUTES: Record<string, never> = {}

const useSplitSellerAttributes = (
  attributes: object = EMPTY_SPLIT_ATTRIBUTES
) => {
  const { sellerData } = useSeller()
  const sellerDataFromStorage: StoredSellerData = JSON.parse(
    localStorage.getItem(SELLER_DATA) ?? '{}'
  ) as StoredSellerData

  const pocId =
    sellerData?.seller?.id ?? sellerDataFromStorage?.seller?.id ?? -1
  const province =
    sellerData?.seller?.address?.provinceCode ??
    sellerDataFromStorage?.seller?.address?.provinceCode ??
    ''
  const city =
    sellerData?.seller?.address?.city ??
    sellerDataFromStorage?.seller?.address?.city ??
    ''

  const splitAttributes = useMemo(
    () => ({ ...attributes, poc_id: pocId, province, city }),
    [attributes, city, pocId, province]
  )

  return {
    pocId,
    splitAttributes,
  }
}

export default function useFeatureToggle({
  splitName,
  attributes = {},
  includeLoadingState = false,
  includeConfig = false,
}: Props) {
  const { pocId, splitAttributes } = useSplitSellerAttributes(attributes)

  const {
    treatment: { treatment = '', config = null },
    isReady = false,
  }: IUseTreatmentWithConfigResult = useTreatmentWithConfig({
    name: splitName,
    attributes: splitAttributes,
    splitKey: process.env.SPLIT_IO_KEY,
  })

  if (includeLoadingState && (!pocId || !isReady)) {
    return FeatureToggle.LOADING
  }

  if (includeConfig && isReady) {
    return { treatment, config }
  }
  return isReady ? treatment : FeatureToggle.OFF
}

export type FeatureFlagOnReader = (nameFlag: string) => boolean | undefined

export const useFeatureFlagOn = (
  attributes: object = EMPTY_SPLIT_ATTRIBUTES
): FeatureFlagOnReader => {
  const { client, isReady } = useSplitClient({
    splitKey: process.env.SPLIT_IO_KEY,
  })
  const { pocId, splitAttributes } = useSplitSellerAttributes(attributes)

  return useCallback(
    (nameFlag: string) => {
      if (!pocId || !isReady || !client) {
        return undefined
      }

      const treatment = client.getTreatment(nameFlag, splitAttributes)
      return (treatment as FeatureToggle) === FeatureToggle.ON
    },
    [client, isReady, pocId, splitAttributes]
  )
}
